import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ShieldCheck, Plus, X, Search, CheckCircle2, UserCheck, Siren, OctagonAlert,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { signedEvidenceUrl } from "@/features/hse/evidenceUpload";
import { ChainOfEvents, InvestigationCard, FormalReportCard, ReportEvidence, VoidIncidentButton, useIncidentTimeline, useReportEvidence, reportEvidenceDataUrl } from "@/features/hse/IncidentReportPanel";
import { VehicleAccidentCard, useVehicleDetails } from "@/features/hse/VehicleAccidentCard";
import { photoTagLabel } from "@/features/hse/vehicleAccidentShared";
import { Card, Pill, Button, Input, Select, Textarea, KPI, Spinner, EmptyState, type Tone } from "@/components/ui";

// =============================================================================
// Incident / Injury Register — T-423 (+ T-423c full-page detail view)
// =============================================================================
// James on the drawer version: "compressed all into a side tab… don't be
// afraid to take up more of the page or all of it". The detail view is now a
// full-page takeover: wide two-column layout (facts + evidence on the left,
// tracking / actions / close-out rail on the right), close box top-right.
//
// Mig 418 (5 Oct 2026) — the FORMAL REPORT. James: "the client needs a chain
// of events even for the day … could have been set out better with a
// timeline." Left column gains the Chain of events (timed entries, or pulled
// from the day's start card / sign-ons / SWMS / diary) and the Investigation
// write-up; the rail gains the Formal incident report card (Download PDF —
// incidentReportPdf.ts — and the director's Sign off). Pieces live in
// IncidentReportPanel.tsx.
//
// Mig 425 (8 Oct 2026) — "Raise a stand-down" in the header (manager+): opens
// the stand-down form with the project and reason pre-filled.
// Mig 428 (8 Oct 2026, Ops brief 6 piece 2b) — CORRECTIVE ACTIONS AND ALERTS.
// Publishing an alert raised from this incident adds its corrective action by
// itself ("Issue HSE alert ALT-… — all crew acknowledged"). The action shows
// the alert and the live "N of M acknowledged"; when everyone on the alert's
// list has acknowledged, the DATABASE closes it and freezes an acknowledgement
// record, which downloads from the action. An action a manager typed by hand
// can be tied to one of the incident's alerts from the small picker under it.
// Gap 8 (8 Oct 2026): the immediate actions from the phone wizard can be
// corrected here afterwards; chain entries are editable (IncidentReportPanel).
// =============================================================================

type IncidentRow = {
  incident_id: string;
  incident_number: string;
  severity: string;
  notifiable_status: string;
  notifiable_to_regulator: boolean | null;
  regulator_name: string | null;
  notification_deadline: string | null;
  regulator_notified: boolean;
  regulator_reference: string | null;
  regulator_notified_at: string | null;
  project_id: string | null;
  site_id: string | null;
  occurred_at: string;
  reported_at: string;
  short_description: string;
  full_description: string;
  immediate_actions: string | null;
  persons_involved: any;
  witnesses: any;
  investigator_employee_id: string | null;
  investigation_started_at: string | null;
  investigation_completed_at?: string | null;
  root_cause: string | null;
  // mig 418 — the formal report
  contributing_factors?: any;
  investigation_summary?: string | null;
  lessons_learned?: string | null;
  client_reference?: string | null;
  client_notified_at?: string | null;
  signed_off_by?: string | null;
  signed_off_at?: string | null;
  report_revision?: number;
  status: string;
  closed_at: string | null;
  submission_id: string | null;
  incident_type: string | null;
  projects: { project_code: string; project_name: string; clients?: { legal_name: string | null } | null } | null;
  sites: { site_name: string; suburb?: string | null; state?: string | null } | null;
  reporter: { first_name: string | null; last_name: string | null } | null;
  investigator: { first_name: string | null; last_name: string | null } | null;
};

function empName(e?: { first_name?: string | null; last_name?: string | null } | null): string | null {
  if (!e) return null;
  const n = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim();
  return n || null;
}

type ActionRow = {
  action_id: string;
  action_description: string;
  action_type: string | null;
  assigned_to_employee_id: string | null;
  due_date: string | null;
  status: string;
  completed_at: string | null;
  completion_evidence: string | null;
  alert_id?: string | null;        // mig 428 — the alert whose acknowledgements close this action
  ack_record_id?: string | null;   // mig 428 — the record frozen when it closed itself
  assignee: { first_name: string | null; last_name: string | null } | null;
};

const SEVERITY_TONE: Record<string, Tone> = {
  report_only: "default",   // mig 411 — for the record, not a near miss
  near_miss: "notice",
  property_damage: "notice", // mig 482 — a vehicle accident or property damage with nobody hurt
  environmental: "notice",   // mig 485 — a spill, dust or noise event with nobody hurt
  first_aid: "pending",
  medical_treatment: "warning",
  lost_time: "warning",
  serious: "critical",
  notifiable: "critical",
  fatality: "critical",
};

const STATUS_TONE: Record<string, Tone> = {
  reported: "open",
  under_investigation: "pending",
  actions_pending: "warning",
  closed: "closed",
  rejected_duplicate: "closed",
};

const OPEN_STATUSES = ["reported", "under_investigation", "actions_pending"];

function fmtDate(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDateTime(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}
function nice(s: string) { return s.replace(/_/g, " "); }

// Persons/witnesses: v2 payloads are structured [{name, company, injury?}];
// legacy rows were free text wrapped in arrays. Render both gracefully.
function renderPeople(v: any): string {
  if (!v) return "—";
  try {
    const arr = Array.isArray(v) ? v : [v];
    const parts = arr
      .map((p: any) => {
        if (p == null) return null;
        if (typeof p === "string") return p.trim();
        if (typeof p === "object" && p.name) {
          let s = p.name;
          if (p.company) s += ` (${p.company})`;
          if (p.injury && typeof p.injury === "object") {
            const inj = [
              p.injury.nature,
              (p.injury.body_parts ?? []).join("/"),
              p.injury.treatment ? `treatment: ${String(p.injury.treatment).replace(/_/g, " ")}` : "",
            ].filter(Boolean).join(", ");
            if (inj) s += ` — ${inj}`;
          }
          return s;
        }
        if (typeof p === "object") return JSON.stringify(p);
        return String(p);
      })
      .filter((s): s is string => !!s && s !== '""' && s !== "null" && s !== "{}");
    return parts.length ? parts.join("; ") : "—";
  } catch { return String(v); }
}

export default function IncidentRegisterScreen() {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"open" | "notifiable" | "closed" | "all">("open");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: incidents, isLoading } = useQuery({
    queryKey: ["incident_register"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("incidents")
        .select(`
          incident_id, incident_number, severity, notifiable_status,
          notifiable_to_regulator, regulator_name, notification_deadline,
          regulator_notified, regulator_reference, regulator_notified_at,
          project_id, site_id, occurred_at, reported_at,
          short_description, full_description, immediate_actions,
          persons_involved, witnesses,
          investigator_employee_id, investigation_started_at, investigation_completed_at, root_cause,
          contributing_factors, investigation_summary, lessons_learned, client_reference, client_notified_at,
          signed_off_by, signed_off_at, report_revision,
          status, closed_at, submission_id, incident_type,
          projects:project_id (project_code, project_name, clients:client_id (legal_name)),
          sites:site_id (site_name, suburb, state),
          reporter:employees!incidents_reported_by_employee_id_fkey (first_name, last_name),
          investigator:employees!incidents_investigator_employee_id_fkey (first_name, last_name)
        `)
        .order("reported_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as IncidentRow[];
    },
  });

  const rows = useMemo(() => {
    let list = incidents ?? [];
    if (filter === "open")       list = list.filter(i => OPEN_STATUSES.includes(i.status));
    if (filter === "closed")     list = list.filter(i => !OPEN_STATUSES.includes(i.status));
    if (filter === "notifiable") list = list.filter(i => i.notifiable_to_regulator);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(i => [
        i.incident_number, i.short_description, i.severity,
        i.projects?.project_code ?? "", i.sites?.site_name ?? "",
        empName(i.reporter) ?? "", empName(i.investigator) ?? "",
      ].join(" ").toLowerCase().includes(q));
    }
    return list;
  }, [incidents, filter, search]);

  const kpis = useMemo(() => {
    const all = incidents ?? [];
    const open = all.filter(i => OPEN_STATUSES.includes(i.status));
    return {
      open: open.length,
      investigating: all.filter(i => i.status === "under_investigation").length,
      notifiablePending: all.filter(i => i.notifiable_to_regulator && !i.regulator_notified && OPEN_STATUSES.includes(i.status)).length,
      unassigned: open.filter(i => !i.investigator_employee_id).length,
    };
  }, [incidents]);

  const openIncident = (incidents ?? []).find(i => i.incident_id === openId) ?? null;

  // Full-page detail takes over the route's content entirely.
  if (openIncident) {
    return (
      <IncidentDetailPage
        incident={openIncident}
        canManage={canManage}
        isDirector={hasRole(user, "director")}
        preparedBy={user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || user.login_email : null}
        currentEmployeeId={user?.employee_id ?? null}
        onClose={() => setOpenId(null)}
        onChanged={() => qc.invalidateQueries({ queryKey: ["incident_register"] })}
      />
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">HSE</div>
          <h1 className="text-2xl font-semibold tracking-tight">Incident register</h1>
          <p className="text-text-muted text-[13px] mt-1">
            Every reported incident and injury — who's tracking it, corrective actions, regulator status.
          </p>
        </div>
        <Link to="/forms/run/INCIDENT">
          <Button icon={Plus}>Report an incident</Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Open" value={String(kpis.open)} icon={AlertTriangle} />
        <KPI label="Under investigation" value={String(kpis.investigating)} icon={Search} />
        <KPI label="Notifiable — regulator pending" value={String(kpis.notifiablePending)} icon={ShieldCheck}
          accent={kpis.notifiablePending > 0} sub={kpis.notifiablePending > 0 ? "notify immediately (WHS s38)" : undefined} />
        <KPI label="No investigator yet" value={String(kpis.unassigned)} icon={UserCheck}
          sub={kpis.unassigned > 0 ? "assign someone to track it" : undefined} />
      </div>

      <Card>
        <div className="p-3 flex items-center gap-2 flex-wrap">
          {([["open", "Open"], ["notifiable", "Notifiable"], ["closed", "Closed"], ["all", "Everything"]] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)}
              className={`text-[12px] px-2.5 py-1 rounded-full border transition ${filter === k ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
              {label}
            </button>
          ))}
          <div className="ml-auto w-full md:w-[280px]">
            <Input placeholder="Search number, site, person…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
      </Card>

      {isLoading && <div className="flex justify-center py-10"><Spinner size={20}/></div>}

      {!isLoading && rows.length === 0 && (
        <EmptyState icon={ShieldCheck} title="Nothing here"
          description={filter === "open" ? "No open incidents — good news. Reports land here the moment anyone submits the incident form (even from a black spot — offline reports sync in)." : "No incidents match this filter."} />
      )}

      {!isLoading && rows.length > 0 && (
        <Card>
          <div className="divide-y divide-line/40">
            {rows.map(i => {
              const overdueNotify = i.notifiable_to_regulator && !i.regulator_notified
                && i.notification_deadline && new Date(i.notification_deadline) < new Date();
              return (
                <button key={i.incident_id} type="button" onClick={() => setOpenId(i.incident_id)}
                  className="w-full text-left px-4 py-3 hover:bg-ink-raised/40 transition flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-[12px] text-accent w-[92px] flex-shrink-0">{i.incident_number}</span>
                  {i.incident_type && <Pill label={nice(i.incident_type)} tone="default" small/>}
                  <Pill label={nice(i.severity)} tone={SEVERITY_TONE[i.severity] ?? "default"} small/>
                  <span className="text-[13px] text-text flex-1 min-w-[200px] truncate">{i.short_description}</span>
                  {i.notifiable_to_regulator && !i.regulator_notified && (
                    <Pill label={overdueNotify ? "notify overdue" : "notifiable"} tone={overdueNotify ? "overdue" : "critical"} small/>
                  )}
                  <span className="text-[12px] text-text-muted hidden md:inline w-[150px] truncate">
                    {i.sites?.site_name ?? i.projects?.project_code ?? "—"}
                  </span>
                  <span className="text-[12px] text-text-muted w-[86px]">{fmtDate(i.occurred_at)}</span>
                  <span className="text-[12px] w-[140px] truncate hidden lg:inline" title="Who's tracking it">
                    {empName(i.investigator)
                      ? <span className="text-text">{empName(i.investigator)}</span>
                      : OPEN_STATUSES.includes(i.status)
                        ? <span className="text-warn">unassigned</span>
                        : <span className="text-text-muted">—</span>}
                  </span>
                  <Pill label={nice(i.status)} tone={STATUS_TONE[i.status] ?? "default"} small/>
                </button>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Full-page incident detail — T-423c
// =============================================================================
function IncidentDetailPage({ incident, canManage, isDirector, preparedBy, currentEmployeeId, onClose, onChanged }: {
  incident: IncidentRow;
  canManage: boolean;
  isDirector: boolean;
  preparedBy: string | null;
  currentEmployeeId: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const isInvestigator = !!currentEmployeeId && incident.investigator_employee_id === currentEmployeeId;
  const canWork = canManage || isInvestigator;
  const isOpen = OPEN_STATUSES.includes(incident.status);
  const [err, setErr] = useState<string | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ["employee_names_active"],
    queryFn: async () => {
      const { data } = await supabase.from("v_employee_names")
        .select("employee_id, full_name").eq("is_active", true).order("full_name");
      return (data ?? []) as { employee_id: string; full_name: string }[];
    },
  });

  // Evidence photos live on the originating form submission (hse-evidence
  // bucket paths in field_values.photos — T-423b).
  const { data: evidence = [] } = useQuery({
    queryKey: ["incident_evidence", incident.submission_id],
    enabled: !!incident.submission_id,
    queryFn: async () => {
      const { data } = await supabase.from("form_submissions")
        .select("field_values").eq("submission_id", incident.submission_id!).maybeSingle();
      const photos = ((data?.field_values as any)?.photos ?? []) as { path: string; tag?: string | null; captured_at?: string | null; taken_known?: boolean | null; caption?: string | null; file_name?: string | null }[];
      const out: { path: string; url: string | null; tag: string | null; captured_at: string | null; taken_known: boolean; caption: string | null }[] = [];
      for (const p of photos) {
        // tag: mig 482; captured_at / taken_known: 2 Nov 2026 — "taken" only when the date is the photo's own EXIF
        if (p?.path) out.push({ path: p.path, url: await signedEvidenceUrl(p.path), tag: p.tag ?? null, captured_at: p.captured_at ?? null, taken_known: !!p.taken_known, caption: p.caption ?? (p.tag === "injury" ? p.file_name ?? null : null) });
      }
      return out;
    },
  });

  const { data: actions = [], refetch: refetchActions } = useQuery({
    queryKey: ["incident_actions", incident.incident_id],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("incident_actions")
        .select(`
          action_id, action_description, action_type, assigned_to_employee_id,
          due_date, status, completed_at, completion_evidence, alert_id, ack_record_id,
          assignee:employees!incident_actions_assigned_to_employee_id_fkey (first_name, last_name)
        `)
        .eq("incident_id", incident.incident_id)
        .order("due_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as ActionRow[];
    },
  });

  // Mig 428 — the alerts raised from this incident and, for each, how many on
  // its list have acknowledged. An action linked to an alert shows that count
  // and closes itself at 100 % (the database does the closing).
  const { data: incAlerts = [] } = useQuery({
    queryKey: ["incident_alerts", incident.incident_id],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("hse_alerts")
        .select("alert_id, alert_number, title, status, severity").eq("incident_id", incident.incident_id).order("created_at");
      if (error) throw error;
      const alerts = (data ?? []) as { alert_id: string; alert_number: string; title: string; status: string; severity: string }[];
      const counts: Record<string, { total: number; done: number }> = {};
      const live = alerts.filter(a => a.status === "published" && a.severity !== "info").map(a => a.alert_id);
      if (live.length > 0) {
        const { data: rows } = await (supabase as any).from("v_hse_alert_recipients").select("alert_id, ack_outstanding").in("alert_id", live);
        for (const r of (rows ?? []) as { alert_id: string; ack_outstanding: boolean }[]) {
          const c = counts[r.alert_id] ?? (counts[r.alert_id] = { total: 0, done: 0 });
          c.total += 1; if (!r.ack_outstanding) c.done += 1;
        }
      }
      return alerts.map(a => ({ ...a, count: counts[a.alert_id] ?? null }));
    },
  });
  const linkedAlertIds = new Set(actions.map(a => a.alert_id).filter(Boolean) as string[]);
  const linkableAlerts = incAlerts.filter(a => a.status === "published" && a.severity !== "info" && !linkedAlertIds.has(a.alert_id));
  // 2 Nov 2026 — the sign-off card says what it needs (the map on a vehicle accident)
  const { data: vehicleDetails } = useVehicleDetails(incident.incident_id, incident.incident_type === "vehicle");
  // gap 8 — immediate actions correctable after the wizard
  const [editingImmediate, setEditingImmediate] = useState(false);
  const [immediateDraft, setImmediateDraft] = useState("");
  async function linkActionToAlert(a: ActionRow, alertId: string) {
    if (!alertId) return;
    setErr(null);
    const { error } = await (supabase as any).from("incident_actions").update({ alert_id: alertId }).eq("action_id", a.action_id);
    if (error) { setErr(error.message); return; }
    refetchActions();
    qc.invalidateQueries({ queryKey: ["incident_alerts", incident.incident_id] });
  }
  async function downloadAckRecord(recordId: string) {
    setErr(null);
    try {
      const { data: rec, error } = await (supabase as any).from("hse_alert_ack_records")
        .select("record_id, alert_id, revision, issued_at, issued_by_name, targeted, acknowledged, outstanding, snapshot").eq("record_id", recordId).single();
      if (error) throw error;
      const { exportAckRecordPdf } = await import("@/features/alerts/alertAckRecordPdf");   // heavy — loaded at the click
      await exportAckRecordPdf(rec);
    } catch (e: any) { setErr(e?.message ?? String(e)); }
  }

  async function patch(fields: Record<string, any>) {
    setErr(null);
    const { error } = await supabase.from("incidents").update(fields).eq("incident_id", incident.incident_id);
    if (error) { setErr(error.message); return false; }
    onChanged();
    return true;
  }

  // mig 418 — the formal report PDF, built from what is on this page.
  const { data: timeline = [] } = useIncidentTimeline(incident.incident_id);
  const { data: reportEvidence = [] } = useReportEvidence(incident.incident_id);
  const { data: signerName } = useQuery({
    queryKey: ["user_name", incident.signed_off_by ?? null],
    enabled: !!incident.signed_off_by,
    queryFn: async () => {
      const { data } = await (supabase as any).from("users").select("employee:employees(first_name, last_name)").eq("user_id", incident.signed_off_by).maybeSingle();
      return data?.employee ? `${data.employee.first_name ?? ""} ${data.employee.last_name ?? ""}`.trim() : null;
    },
  });
  async function downloadReport() {
    const { exportIncidentReportPdf, imageToDataUrl } = await import("@/features/hse/incidentReportPdf");
    const photos: Array<{ dataUrl: string; caption?: string | null }> = [];
    for (const p of evidence) {
      if (!p.url) continue;
      const d = await imageToDataUrl(p.url);
      // 2 Nov 2026 — no watermark any more; the caption says what is actually known about when
      if (d) photos.push({ dataUrl: d, caption: p.caption ?? (p.taken_known ? `Taken ${p.captured_at ? fmtDate(p.captured_at) : ""} — from the reporter's phone` : `Uploaded by the reporter${p.captured_at ? ` ${fmtDate(p.captured_at)}` : ""}`) });
    }
    for (const f of reportEvidence) {
      const d = await reportEvidenceDataUrl(f.file_id);
      if (d) photos.push({ dataUrl: d, caption: `${f.change_summary ?? f.file_name ?? ""}${f.version_label ? ` — taken ${fmtDate(f.version_label)}` : ""}`.trim() });
    }
    await exportIncidentReportPdf({
      incidentNumber: incident.incident_number, title: incident.short_description,
      severity: incident.severity, incidentType: incident.incident_type, status: incident.status,
      occurredAt: incident.occurred_at, reportedAt: incident.reported_at, reportedBy: empName(incident.reporter),
      projectCode: incident.projects?.project_code, projectName: incident.projects?.project_name,
      siteName: incident.sites?.site_name, siteAddress: incident.sites?.suburb ?? null, state: incident.sites?.state ?? null,
      clientName: incident.projects?.clients?.legal_name ?? null,
      fullDescription: incident.full_description,
      personsInvolved: renderPeople(incident.persons_involved), witnesses: renderPeople(incident.witnesses),
      immediateActions: incident.immediate_actions,
      timeline: timeline.map(t => ({ happened_at: t.happened_at, entry: t.entry, who: t.who, source: t.source })),
      investigator: empName(incident.investigator), investigationStartedAt: incident.investigation_started_at, investigationCompletedAt: incident.investigation_completed_at ?? null,
      investigationSummary: incident.investigation_summary ?? null, rootCause: incident.root_cause,
      contributingFactors: Array.isArray(incident.contributing_factors) ? incident.contributing_factors : null,
      lessonsLearned: incident.lessons_learned ?? null,
      actions: actions.map(a => ({ action_description: a.action_description, assignee: empName(a.assignee), due_date: a.due_date, status: a.status, completed_at: a.completed_at, completion_evidence: a.completion_evidence })),
      notifiable: incident.notifiable_to_regulator, regulatorName: incident.regulator_name, regulatorNotified: incident.regulator_notified,
      regulatorReference: incident.regulator_reference, regulatorNotifiedAt: incident.regulator_notified_at,
      clientReference: incident.client_reference ?? null, clientNotifiedAt: incident.client_notified_at ?? null,
      photos,
      signedOffBy: signerName ?? null, signedOffAt: incident.signed_off_at ?? null, reportRevision: incident.report_revision ?? 0,
      preparedBy,
    });
  }

  const [assigning, setAssigning] = useState(false);
  const [assignee, setAssignee] = useState("");
  async function assignInvestigator() {
    if (!assignee) return;
    const ok = await patch({
      investigator_employee_id: assignee,
      investigation_started_at: incident.investigation_started_at ?? new Date().toISOString(),
      status: incident.status === "reported" ? "under_investigation" : incident.status,
    });
    if (ok) setAssigning(false);
  }

  const [notifying, setNotifying] = useState(false);
  const [regRef, setRegRef] = useState("");
  async function markNotified() {
    const ok = await patch({
      regulator_notified: true,
      regulator_reference: regRef.trim() || null,
      regulator_notified_at: new Date().toISOString(),
      notifiable_status: "notified",
    });
    if (ok) setNotifying(false);
  }

  const [closing, setClosing] = useState(false);
  const [rootCause, setRootCause] = useState(incident.root_cause ?? "");
  async function closeIncident() {
    const openActions = actions.filter(a => ["open", "in_progress", "overdue"].includes(a.status));
    if (openActions.length > 0 && !window.confirm(`${openActions.length} corrective action(s) still open. Close the incident anyway?`)) return;
    const ok = await patch({
      status: "closed",
      closed_at: new Date().toISOString(),
      root_cause: rootCause.trim() || null,
      investigation_completed_at: incident.investigation_started_at ? new Date().toISOString() : null,
    });
    if (ok) setClosing(false);
  }

  const [addingAction, setAddingAction] = useState(false);
  const [actDesc, setActDesc] = useState("");
  const [actAssignee, setActAssignee] = useState("");
  const [actDue, setActDue] = useState("");
  async function addAction() {
    if (!actDesc.trim()) return;
    setErr(null);
    const { error } = await supabase.from("incident_actions").insert({
      incident_id: incident.incident_id,
      action_description: actDesc.trim(),
      action_type: "corrective",
      assigned_to_employee_id: actAssignee || null,
      due_date: actDue || null,
      status: "open",
    });
    if (error) { setErr(error.message); return; }
    setActDesc(""); setActAssignee(""); setActDue(""); setAddingAction(false);
    refetchActions();
    if (incident.status === "under_investigation") await patch({ status: "actions_pending" });
  }
  async function completeAction(a: ActionRow) {
    const evidenceNote = window.prompt("What was done? (completion evidence — optional)") ?? "";
    const { error } = await supabase.from("incident_actions")
      .update({ status: "complete", completed_at: new Date().toISOString(), completion_evidence: evidenceNote.trim() || null })
      .eq("action_id", a.action_id);
    if (error) { setErr(error.message); return; }
    refetchActions();
    qc.invalidateQueries({ queryKey: ["incident_register"] });
  }

  const notifyOverdue = incident.notifiable_to_regulator && !incident.regulator_notified
    && incident.notification_deadline && new Date(incident.notification_deadline) < new Date();

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button type="button" onClick={onClose} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-2 hover:text-text">
            ← Incident register
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[14px] text-accent">{incident.incident_number}</span>
            {incident.incident_type && <Pill label={nice(incident.incident_type)} tone="default"/>}
            <Pill label={nice(incident.severity)} tone={SEVERITY_TONE[incident.severity] ?? "default"}/>
            <Pill label={nice(incident.status)} tone={STATUS_TONE[incident.status] ?? "default"}/>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">{incident.short_description}</h1>
          <div className="text-[13px] text-text-muted mt-0.5">
            {incident.projects ? `${incident.projects.project_code} · ` : ""}{incident.sites?.site_name ?? incident.projects?.project_name ?? "No site"}
            {" · occurred "}{fmtDateTime(incident.occurred_at)}
            {" · reported by "}{empName(incident.reporter) ?? "—"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Raise an HSE alert off this incident (mig 413) — opens the alert
              editor pre-filled from the register; source = incident. */}
          {canManage && (
            <Link to={`/hse/alerts/new?incident=${incident.incident_id}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-2 text-[12px] text-text hover:border-accent/50 hover:text-accent">
              <Siren size={14}/> Raise an HSE alert
            </Link>
          )}
          {/* Raise a stand-down off this incident (mig 425, 8 Oct 2026) — opens
              the stand-down form with the project and reason pre-filled and the
              incident linked. The schedule then refuses bookings on the site. */}
          {canManage && incident.project_id && (
            <Link to={`/hse/standdowns/new?incident=${incident.incident_id}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-2 text-[12px] text-text hover:border-danger/50 hover:text-danger">
              <OctagonAlert size={14}/> Raise a stand-down
            </Link>
          )}
          <button type="button" onClick={onClose}
            className="text-text-muted hover:text-text p-2 rounded-md border border-line hover:bg-ink-raised" aria-label="Close">
            <X size={18}/>
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>
      )}

      {/* Notifiable banner — full width, impossible to miss */}
      {incident.notifiable_to_regulator && (
        <div className={`rounded-md border p-4 space-y-2 ${incident.regulator_notified ? "border-ok/30 bg-ok/8" : "border-danger/40 bg-danger/10"}`}>
          {incident.regulator_notified ? (
            <div className="text-[13px] text-ok">
              <CheckCircle2 size={14} className="inline mr-1.5 -mt-0.5"/>
              Regulator notified {fmtDateTime(incident.regulator_notified_at)}
              {incident.regulator_reference && <> · ref <span className="font-mono">{incident.regulator_reference}</span></>}
            </div>
          ) : (
            <>
              <div className="text-[13px] text-danger font-medium">
                <AlertTriangle size={14} className="inline mr-1.5 -mt-0.5"/>
                Likely notifiable — {incident.regulator_name ?? "the regulator"} must be told immediately (WHS Act s38).
                {incident.notification_deadline && (
                  <span className={notifyOverdue ? " font-semibold" : ""}>
                    {" "}Internal deadline {fmtDateTime(incident.notification_deadline)}{notifyOverdue ? " — OVERDUE" : ""}.
                  </span>
                )}
              </div>
              <div className="text-[12px] text-text-muted">Phone first, then record it here. Preserve the site until the inspector says otherwise.</div>
              {canWork && !notifying && (
                <Button size="sm" variant="danger" onClick={() => setNotifying(true)}>Mark regulator notified</Button>
              )}
              {notifying && (
                <div className="flex items-end gap-2 flex-wrap">
                  <label className="text-[11px] text-text-muted flex-1 min-w-[220px]">Regulator reference (if given)
                    <Input value={regRef} onChange={e => setRegRef(e.target.value)} placeholder="e.g. WHSQ notification #"/>
                  </label>
                  <Button size="sm" onClick={markNotified}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setNotifying(false)}>Cancel</Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Two-column body: the facts left, the workflow right */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 items-start">

        {/* LEFT — what happened */}
        <div className="space-y-4 min-w-0">
          <Card>
            <div className="p-4 space-y-4">
              <div className="text-[11px] uppercase tracking-wider text-text-muted">What happened</div>
              <div className="text-[13px] text-text whitespace-pre-wrap leading-relaxed">{incident.full_description}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 text-[13px] border-t border-line/40 pt-4">
                <Dv label="Occurred" value={fmtDateTime(incident.occurred_at)}/>
                <Dv label="Reported" value={fmtDateTime(incident.reported_at)}/>
                <Dv label="Reported by" value={empName(incident.reporter) ?? "—"}/>
                <Dv label="Project" value={incident.projects ? `${incident.projects.project_code}` : "—"}/>
                <Dv label="Site" value={incident.sites?.site_name ?? "—"}/>
                <Dv label="Notifiable status" value={nice(incident.notifiable_status)}/>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4 space-y-3">
              <div className="text-[11px] uppercase tracking-wider text-text-muted">People</div>
              <Dv label="Persons involved" value={renderPeople(incident.persons_involved)} block/>
              <Dv label="Witnesses" value={renderPeople(incident.witnesses)} block/>
              {/* 8 Oct 2026 (Ops brief 6, gap 8) — immediate actions come from the
                  phone wizard and could not be corrected afterwards. Now they can
                  (manager+ or the investigator); an edit after sign-off puts the
                  report back to draft, like any other edit to what it prints. */}
              {editingImmediate ? (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Immediate actions taken</div>
                  <Textarea rows={4} value={immediateDraft} onChange={e => setImmediateDraft(e.target.value)} placeholder="What was done on the day, straight after it happened"/>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={async () => { if (await patch({ immediate_actions: immediateDraft.trim() || null })) setEditingImmediate(false); }}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingImmediate(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div>
                  {incident.immediate_actions
                    ? <Dv label="Immediate actions taken" value={incident.immediate_actions} block/>
                    : canWork ? <div className="text-[10px] uppercase tracking-wider text-text-muted">Immediate actions taken</div> : null}
                  {canWork && (
                    <button type="button" onClick={() => { setImmediateDraft(incident.immediate_actions ?? ""); setEditingImmediate(true); }} className="mt-1 text-[11px] text-accent hover:underline">
                      {incident.immediate_actions ? "Correct the immediate actions" : "Add the immediate actions"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* mig 482 — the vehicle-accident facts, only on that type */}
          {incident.incident_type === "vehicle" && (
            <VehicleAccidentCard incidentId={incident.incident_id} canWork={canWork}
              report={{ incident: incident as any, reportedBy: empName(incident.reporter), witnesses: renderPeople(incident.witnesses), signerName: signerName ?? null, preparedBy }}/>
          )}

          {evidence.length > 0 && (
            <Card>
              <div className="p-4">
                <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2.5">Evidence photos</div>
                <div className="flex flex-wrap gap-3">
                  {evidence.map(p => p.url ? (
                    <a key={p.path} href={p.url} target="_blank" rel="noreferrer" className="block w-32">
                      <img src={p.url} alt="" className="w-32 h-32 object-cover rounded-md border border-line hover:border-accent transition"/>
                      {photoTagLabel(p.tag) && <div className="text-[10px] text-text-muted mt-1 truncate">{photoTagLabel(p.tag)}</div>}
                    </a>
                  ) : (
                    <div key={p.path} className="w-32 h-32 rounded-md border border-line bg-ink flex items-center justify-center text-[11px] text-text-muted text-center px-2">not yet synced from the phone</div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* mig 418 — the chain of events and the investigation write-up */}
          <ChainOfEvents incidentId={incident.incident_id} projectId={incident.project_id} occurredAt={incident.occurred_at} canWork={canWork}/>
          <InvestigationCard incident={incident} canWork={canWork} onPatch={patch}/>
          <ReportEvidence incidentId={incident.incident_id} occurredAt={incident.occurred_at} canWork={canWork} vehicle={incident.incident_type === "vehicle"}/>
        </div>

        {/* RIGHT — the workflow rail */}
        <div className="space-y-4">
          <FormalReportCard
            incidentId={incident.incident_id}
            signedOffAt={incident.signed_off_at ?? null}
            signedOffBy={incident.signed_off_by ?? null}
            reportRevision={incident.report_revision ?? 0}
            isDirector={isDirector}
            onDownload={downloadReport}
            onSigned={onChanged}
            factorCount={Array.isArray(incident.contributing_factors) ? incident.contributing_factors.length : 0}
            actionCount={actions.filter(a => a.status !== "cancelled").length}
            vehicle={incident.incident_type === "vehicle"}
            hasMap={!!vehicleDetails?.sketch_path}
            hasFindings={!!(incident.root_cause?.trim() || incident.investigation_summary?.trim())}
          />
          <Card>
            <div className="p-4 space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-text-muted">Who's tracking it</div>
              {incident.investigator ? (
                <div className="text-[13px]">
                  <span className="text-text font-medium">{empName(incident.investigator)}</span>
                  <div className="text-[12px] text-text-muted">investigating since {fmtDate(incident.investigation_started_at)}</div>
                  {canManage && isOpen && (
                    <button type="button" onClick={() => setAssigning(v => !v)} className="text-[12px] text-accent hover:underline mt-1">Reassign</button>
                  )}
                </div>
              ) : (
                <div className="text-[13px] text-warn">
                  Nobody assigned yet.
                  {canManage && isOpen && (
                    <button type="button" onClick={() => setAssigning(v => !v)} className="block text-accent hover:underline mt-1">Assign an investigator</button>
                  )}
                </div>
              )}
              {assigning && (
                <div className="space-y-2 pt-1">
                  <Select value={assignee} onChange={e => setAssignee(e.target.value)}>
                    <option value="">Pick someone…</option>
                    {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.full_name}</option>)}
                  </Select>
                  <Button size="sm" onClick={assignInvestigator} disabled={!assignee}>Assign</Button>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-text-muted">Corrective actions</div>
                {canWork && isOpen && !addingAction && (
                  <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAddingAction(true)}>Add</Button>
                )}
              </div>
              {actions.length === 0 && !addingAction && (
                <div className="text-[12px] text-text-muted italic">No actions yet{isOpen ? " — what stops this happening again?" : "."}</div>
              )}
              {actions.map(a => {
                const done = a.status === "complete";
                const overdue = !done && a.due_date && a.due_date < new Date().toISOString().slice(0, 10);
                return (
                  <div key={a.action_id} className="bg-ink rounded-md border border-line/50 px-3 py-2 flex items-start gap-2">
                    <button
                      type="button"
                      disabled={done || !canWork}
                      onClick={() => completeAction(a)}
                      title={done ? "Done" : "Mark complete"}
                      className={`mt-0.5 w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center ${done ? "bg-ok/20 border-ok text-ok" : "border-line hover:border-accent"}`}
                    >
                      {done && <CheckCircle2 size={11}/>}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[12px] ${done ? "text-text-muted line-through" : "text-text"}`}>{a.action_description}</div>
                      <div className="text-[11px] text-text-muted">
                        {empName(a.assignee) ?? "unassigned"}
                        {a.due_date && <span className={overdue ? " text-danger" : ""}> · due {fmtDate(a.due_date)}{overdue ? " — overdue" : ""}</span>}
                        {done && a.completed_at && <> · done {fmtDate(a.completed_at)}</>}
                        {a.completion_evidence && <> · {a.completion_evidence}</>}
                      </div>
                      {/* Mig 428 — an action tied to an alert: the live count while it is
                          open, the alert and the frozen record once it has closed itself. */}
                      {a.alert_id && (() => {
                        const al = incAlerts.find(x => x.alert_id === a.alert_id);
                        return (
                          <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap">
                            <Link to={`/hse/alerts/${a.alert_id}`} className="inline-flex items-center gap-1 text-accent hover:underline"><Siren size={11}/> {al?.alert_number ?? "the alert"}</Link>
                            {!done && al?.count && <span className={al.count.done === al.count.total ? "text-ok" : "text-warn"}>{al.count.done} of {al.count.total} acknowledged — closes itself at 100 %</span>}
                            {!done && al && !al.count && <span className="text-text-muted">{al.status === "published" ? "nobody on its list yet" : `alert is ${al.status}`}</span>}
                            {a.ack_record_id && <button type="button" onClick={() => downloadAckRecord(a.ack_record_id!)} className="text-accent hover:underline">Download the acknowledgement record</button>}
                          </div>
                        );
                      })()}
                      {!a.alert_id && !done && canManage && linkableAlerts.length > 0 && (
                        <select value="" onChange={e => linkActionToAlert(a, e.target.value)}
                          className="mt-1 bg-ink border border-line rounded px-1.5 py-0.5 text-[11px] text-text-muted"
                          title="Tie this action to an alert raised from this incident — it then closes itself when everyone on the alert's list has acknowledged.">
                          <option value="">Close this when an alert is fully acknowledged…</option>
                          {linkableAlerts.map(x => <option key={x.alert_id} value={x.alert_id}>{x.alert_number} · {x.title}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                );
              })}
              {addingAction && (
                <div className="bg-ink-raised/40 border border-dashed border-line rounded-md p-3 space-y-2">
                  <Textarea rows={2} placeholder="What needs to happen…" value={actDesc} onChange={e => setActDesc(e.target.value)}/>
                  <Select value={actAssignee} onChange={e => setActAssignee(e.target.value)}>
                    <option value="">Unassigned</option>
                    {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.full_name}</option>)}
                  </Select>
                  <div className="flex items-end gap-2">
                    <label className="text-[10px] text-text-muted flex-1">Due
                      <Input type="date" value={actDue} onChange={e => setActDue(e.target.value)}/>
                    </label>
                    <Button size="sm" onClick={addAction} disabled={!actDesc.trim()}>Add</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAddingAction(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {isOpen && canWork && (
            <Card>
              <div className="p-4 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-text-muted">Close out</div>
                {!closing ? (
                  <div className="flex gap-2 flex-wrap items-center">
                    <Button size="sm" variant="secondary" onClick={() => setClosing(true)}>Close this incident…</Button>
                    <VoidIncidentButton incidentId={incident.incident_id} canManage={canManage} onDone={() => { onChanged(); onClose(); }}/>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="text-[11px] text-text-muted block">Root cause / findings (kept on the record)
                      <Textarea rows={3} value={rootCause} onChange={e => setRootCause(e.target.value)} placeholder="After examination of the facts…"/>
                    </label>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={closeIncident}>Close incident</Button>
                      <Button size="sm" variant="ghost" onClick={() => setClosing(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Dv({ label, value, block = false }: { label: string; value: string; block?: boolean }) {
  return (
    <div className={block ? "" : "min-w-0"}>
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`text-[13px] text-text ${block ? "whitespace-pre-wrap leading-relaxed" : "truncate"}`}>{value}</div>
    </div>
  );
}
